import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { useFrame, ThreeEvent } from '@react-three/fiber';
import type { GameObject as GameObjectData, WorldType } from '@hollowveil/shared';
import {
  useGameStore,
  isInteractableInWorld,
  COLORS,
  getInteractionPosition,
  getInteractionDistance,
} from '@hollowveil/shared';
import type { Group } from 'three';
import { getModelComponent } from './models';
import { getOracleDynamicDialogue, getHatterDynamicDialogue, getHatterSpectralDialogue, getDustinDialogue, getVekeunaDialogue, getEricaDialogue, getDemagordonDialogue, getBikeRackDialogue, getDustinHouseDialogue, hawkinsDynamicDialogues } from '../../data/adventures/reveilEducatrice/riddles';

// Object types that should NOT become transparent (characters, NPCs, collectibles)
// Walkable surfaces like stairs/platforms stay opaque since player walks on them
const NON_OCCLUDING_TYPES = ['spectral_remnant', 'sage', 'npc', 'orb', 'crystal', 'spectral_key', 'magic_flower', 'stairs', 'platform', 'ramp', 'bridge', 'spectral_bridge', 'ancient_seed', 'ancient_stone', 'oracle_chevelu', 'runic_sphere_portal', 'vintage_bike', 'walkie_talkie', 'photo_frame'];

// No-op raycast function for occluding objects (allows clicks to pass through)
const noRaycast = () => null;

// Minimum height for an object to potentially occlude the player (player height ~1.5)
const MIN_OCCLUDING_HEIGHT = 1.0;

// Object types with special animations
const FLOATING_TYPES = ['crystal', 'orb', 'gem', 'spectral_key', 'spectral_remnant', 'ancient_seed'];
const ROTATING_TYPES = ['orb', 'key', 'spectral_key'];
const PULSATING_TYPES = ['crystal', 'orb', 'spectral_remnant', 'magic_flower', 'spectral_key', 'ancient_seed'];
const SWAYING_TYPES = ['tree', 'ancient_tree', 'bush', 'plant', 'flower', 'palm_tree', 'banana_tree', 'giant_fern', 'tropical_flower', 'jungle_vine', 'date_palm', 'pine_tree', 'snowy_pine', 'seaweed', 'reed'];

interface GameObjectProps {
  object: GameObjectData;
  world: WorldType;
}

export function GameObject({ object, world }: GameObjectProps) {
  const {
    player,
    targetPosition,
    setTargetPosition,
    interactWithObject,
    showMessage,
    openDoor,
    addToInventory,
    hideObject,
    isObjectHidden,
    openDoorWithItem,
    showRiddle,
    hasInventoryItem,
    giveItemToNPC,
    setPendingInteraction,
    loadRoom,
    solvedRiddles,
    questComplete,
    completedRooms,
    mountBike,
    dismountBike,
  } = useGameStore();
  const [hovered, setHovered] = useState(false);
  const [isOccluding, setIsOccluding] = useState(false);
  const groupRef = useRef<Group>(null);

  // Check if object should be dynamically activated based on solved riddle or quest completion
  const dynamicProperties = useMemo(() => {
    const props = { ...object.properties };

    // If activateAfterRiddleSolved is set, check if the riddle is solved
    if (props.activateAfterRiddleSolved && typeof props.activateAfterRiddleSolved === 'string') {
      const requiredRiddle = props.activateAfterRiddleSolved;
      const isRiddleSolved = solvedRiddles.has(requiredRiddle);
      props.isActive = isRiddleSolved;
    }

    // If activateOnQuestComplete is set, check if the quest is complete
    if (props.activateOnQuestComplete) {
      props.isActive = questComplete;
    }

    // If activateAfterRoomCompleted is set, check if the room is completed
    if (props.activateAfterRoomCompleted && typeof props.activateAfterRoomCompleted === 'string') {
      const requiredRoom = props.activateAfterRoomCompleted;
      props.isActive = completedRooms.includes(requiredRoom);
    }

    return props;
  }, [object.properties, solvedRiddles, questComplete, completedRooms]);

  // Animation state
  const animationOffset = useRef(Math.random() * Math.PI * 2); // Random offset for variation

  // Check if hidden (for use in hooks and render)
  const isHidden = isObjectHidden(object.id);

  // Object animations and occlusion check
  useFrame((state) => {
    // Skip if hidden
    if (isHidden) return;
    const time = state.clock.elapsedTime + animationOffset.current;

    // Floating animation
    let floatY = 0;
    if (FLOATING_TYPES.includes(object.modelId)) {
      floatY = Math.sin(time * 1.5) * 0.08;
    }

    // Rotation animation
    let rotationY = 0;
    if (ROTATING_TYPES.includes(object.modelId)) {
      rotationY = time * 0.5;
    }

    // Pulsating animation (scale)
    let pulseScale = 1;
    if (PULSATING_TYPES.includes(object.modelId)) {
      pulseScale = 1 + Math.sin(time * 2) * 0.05;
    }

    // Swaying animation (for vegetation)
    let swayRotation = 0;
    if (SWAYING_TYPES.includes(object.modelId)) {
      swayRotation = Math.sin(time * 0.8) * 0.03;
    }

    // Apply animations to group
    if (groupRef.current) {
      groupRef.current.position.y = object.position[1] + floatY;
      if (ROTATING_TYPES.includes(object.modelId)) {
        groupRef.current.rotation.y = rotationY;
      }
      if (SWAYING_TYPES.includes(object.modelId)) {
        groupRef.current.rotation.z = swayRotation;
        groupRef.current.rotation.x = Math.sin(time * 0.6 + 1) * 0.02;
      }
      // Apply scale animation
      const baseScale = object.scale;
      groupRef.current.scale.set(
        baseScale[0] * pulseScale,
        baseScale[1] * pulseScale,
        baseScale[2] * pulseScale
      );
    }
  });

  // Occlusion check in separate useFrame to avoid conflicts
  // Makes objects transparent when they block the camera's view of the player
  useFrame(() => {
    // Skip if hidden
    if (isHidden) return;

    // Skip occlusion check for certain object types (NPCs, collectibles, walkable surfaces)
    // Also skip for npc_ prefixed models
    if (NON_OCCLUDING_TYPES.includes(object.modelId) || object.modelId.startsWith('npc_')) {
      if (isOccluding) setIsOccluding(false);
      return;
    }

    // Only check objects tall enough to actually block the player's view
    const objectHeight = object.scale[1] || 1;
    if (objectHeight < MIN_OCCLUDING_HEIGHT) {
      if (isOccluding) setIsOccluding(false);
      return;
    }

    const objX = object.position[0];
    const objZ = object.position[2];
    const objY = object.position[1];
    const objWidth = object.scale[0] || 1;
    const objDepth = object.scale[2] || 1;

    // Check if a position is occluded by this object
    // Isometric camera is at +X, +Z corner looking toward -X, -Z direction
    // An object occludes if it's between camera and player
    const checkOcclusion = (posX: number, posY: number, posZ: number): boolean => {
      // Skip if player is above the object (standing on something higher)
      const objectTopY = objY + objectHeight;
      if (posY > objectTopY + 0.5) {
        return false;
      }

      // Calculate distance from object to player
      const dx = posX - objX;
      const dz = posZ - objZ;

      // Player must be BEHIND the object (in -X or -Z direction from camera's view)
      // Object is at higher X and/or Z than player for occlusion
      const playerBehindX = dx < -0.3; // Player has lower X (behind object from camera)
      const playerBehindZ = dz < -0.3; // Player has lower Z (behind object from camera)

      // Player must be behind the object in at least one direction
      if (!playerBehindX && !playerBehindZ) {
        return false;
      }

      // For isometric 45°, use perpendicular axis to check screen overlap
      // screenAxis ≈ X - Z (perpendicular to view direction)
      const objectScreen = objX - objZ;
      const playerScreen = posX - posZ;

      // Tight screen-space overlap check based on object size
      // Smaller margin = occlusion only when directly behind
      const screenHalfWidth = (objWidth + objDepth) / 4 + 0.5;

      const screenOverlap = Math.abs(playerScreen - objectScreen) < screenHalfWidth;

      if (!screenOverlap) {
        return false;
      }

      // Player must be reasonably close to the object (not too far behind)
      const maxDistanceBehind = Math.max(objWidth, objDepth) / 2 + 2.5;

      // Check player is within the occlusion zone behind the object
      const behindDistanceX = playerBehindX ? Math.abs(dx) : 0;
      const behindDistanceZ = playerBehindZ ? Math.abs(dz) : 0;
      const behindDistance = Math.max(behindDistanceX, behindDistanceZ);

      return behindDistance < maxDistanceBehind;
    };

    // Check current player position
    const playerOccluded = checkOcclusion(
      player.position[0],
      player.position[1],
      player.position[2]
    );

    // Also check target destination if player is moving there
    const targetOccluded = targetPosition
      ? checkOcclusion(targetPosition[0], player.position[1], targetPosition[2])
      : false;

    const shouldOcclude = playerOccluded || targetOccluded;

    if (shouldOcclude !== isOccluding) {
      setIsOccluding(shouldOcclude);
    }
  });

  const isInteractive =
    object.type !== 'static' && isInteractableInWorld(object, world);

  // Execute interaction actions
  const executeInteraction = useCallback(() => {
    // First, check if any requireItem action exists and if player has the required item
    if (object.onInteract) {
      const requireAction = object.onInteract.find((a) => a.type === 'requireItem');
      if (requireAction) {
        const { itemId, missingMessage } = requireAction.params as {
          itemId: string;
          missingMessage?: string;
        };
        if (!hasInventoryItem(itemId)) {
          showMessage(missingMessage || 'Vous n\'avez pas l\'objet requis...');
          return; // Don't execute other actions
        }
      }
    }

    // Call store's interactWithObject for quest tracking
    interactWithObject(object.id);

    // Execute onInteract actions
    if (object.onInteract) {
      object.onInteract.forEach((action) => {
        // Skip requireItem as it's already handled above
        if (action.type === 'requireItem') return;

        // Show message
        if (action.type === 'showMessage' && typeof action.params.message === 'string') {
          showMessage(action.params.message);
        }
        // Open door
        if (action.type === 'openDoor' && typeof action.params.doorId === 'string') {
          openDoor(action.params.doorId);
        }
        // Add item to inventory
        if (action.type === 'addToInventory') {
          const { itemId, itemName, itemDescription, itemIcon } = action.params as {
            itemId: string;
            itemName: string;
            itemDescription: string;
            itemIcon: string;
          };
          addToInventory({
            id: itemId,
            name: itemName,
            description: itemDescription,
            iconId: itemIcon,
            usableInWorld: 'both',
          });
          // Trigger player celebration animation
          const triggerCelebration = (window as unknown as { triggerPlayerCelebration?: () => void }).triggerPlayerCelebration;
          if (triggerCelebration) triggerCelebration();
        }
        // Hide object (for collected items)
        if (action.type === 'hideObject' && typeof action.params.objectId === 'string') {
          hideObject(action.params.objectId);
        }
        // Mount bike
        if (action.type === 'mountBike') {
          const { color } = action.params as { color: 'blue' | 'yellow' | 'red' };
          mountBike(color);
        }
        // Dismount bike
        if (action.type === 'dismountBike') {
          dismountBike();
        }
        // Open door with required item
        if (action.type === 'openDoorWithItem') {
          const { doorId, requiredItemId } = action.params as {
            doorId: string;
            requiredItemId: string;
          };
          openDoorWithItem(doorId, requiredItemId);
        }
        // Show riddle dialog
        if (action.type === 'showRiddle') {
          const riddle = action.params.riddle as import('@hollowveil/shared').Riddle;
          if (riddle) {
            showRiddle(riddle);
          }
        }
        // Show dynamic riddle based on game state (for Oracle, Hatter, Hawkins NPCs, etc.)
        if (action.type === 'showDynamicRiddle') {
          const { riddleType } = action.params as { riddleType: string };
          if (riddleType === 'oracle') {
            const dynamicRiddle = getOracleDynamicDialogue(completedRooms, solvedRiddles);
            showRiddle(dynamicRiddle);
          } else if (riddleType === 'hatter') {
            const dynamicRiddle = getHatterDynamicDialogue(solvedRiddles, player.inventory, questComplete);
            showRiddle(dynamicRiddle);
          } else if (riddleType === 'hatter_spectral') {
            const dynamicRiddle = getHatterSpectralDialogue(solvedRiddles);
            showRiddle(dynamicRiddle);
          } else if (riddleType === 'dustin') {
            const dynamicRiddle = getDustinDialogue(solvedRiddles, player.inventory, player.onBike, player.currentBikeColor);
            showRiddle(dynamicRiddle);
          } else if (riddleType === 'erica') {
            const dynamicRiddle = getEricaDialogue(solvedRiddles);
            showRiddle(dynamicRiddle);
          } else if (riddleType === 'vekeuna') {
            const dynamicRiddle = getVekeunaDialogue(solvedRiddles);
            showRiddle(dynamicRiddle);
          } else if (riddleType === 'demagordon') {
            const dynamicRiddle = getDemagordonDialogue(solvedRiddles);
            showRiddle(dynamicRiddle);
          } else if (riddleType === 'rift') {
            showRiddle(hawkinsDynamicDialogues.rift_enter);
          } else if (riddleType === 'rift_return') {
            showRiddle(hawkinsDynamicDialogues.rift_return);
          } else if (riddleType === 'bike_rack') {
            const dynamicRiddle = getBikeRackDialogue(player.onBike, solvedRiddles, player.inventory, player.currentBikeColor);
            showRiddle(dynamicRiddle);
          } else if (riddleType === 'dustin_house') {
            const dynamicRiddle = getDustinHouseDialogue(player.onBike, solvedRiddles, player.currentBikeColor);
            showRiddle(dynamicRiddle);
          } else if (riddleType === 'walkie_station') {
            showRiddle(hawkinsDynamicDialogues.walkie_talkie_puzzle);
          }
        }
        // Give item (remove from inventory - gift to NPC)
        if (action.type === 'giveItem') {
          const { itemId, npcId, successMessage } = action.params as {
            itemId: string;
            npcId?: string;
            successMessage?: string;
          };
          const success = giveItemToNPC(itemId, npcId || object.id);
          if (success && successMessage) {
            setTimeout(() => showMessage(successMessage), 500);
          }
        }
        // Place tea item on tile - opens inventory selection
        if (action.type === 'placeTeaItem') {
          const { requiredItemId, placedRiddleId, tileName, tileColor } = action.params as {
            requiredItemId: string;
            placedRiddleId: string;
            tileName: string;
            tileColor: string;
          };

          // Check if already placed
          if (solvedRiddles.has(placedRiddleId)) {
            showMessage(`✓ ${tileName} est déjà en place !`);
            return;
          }

          // Get tea items from inventory
          const teaItemsInfo: Record<string, { name: string; icon: string }> = {
            'tea_item_cup': { name: 'Tasse', icon: '☕' },
            'tea_item_saucer': { name: 'Soucoupe', icon: '🍽️' },
            'tea_item_teapot': { name: 'Théière', icon: '🫖' },
            'tea_item_cake': { name: 'Gâteau', icon: '🎂' },
          };

          const inventoryTeaItems = player.inventory.filter(item =>
            Object.keys(teaItemsInfo).includes(item.id)
          );

          if (inventoryTeaItems.length === 0) {
            showMessage(`🎒 Tu n'as aucun élément de tea party dans ton inventaire. Cherche-les dans le niveau !`);
            return;
          }

          // Create choices from inventory items
          const choices = inventoryTeaItems.map(item => ({
            id: item.id,
            text: `${teaItemsInfo[item.id]?.icon || '📦'} ${teaItemsInfo[item.id]?.name || item.name}`,
            isCorrect: item.id === requiredItemId,
          }));

          // Add cancel option
          choices.push({
            id: 'cancel',
            text: '❌ Annuler',
            isCorrect: false,
          });

          // Show selection dialog
          showRiddle({
            id: `select_${placedRiddleId}`,
            npcId: object.id,
            npcName: tileName,
            question: `*Tu te tiens devant le cercle ${tileColor}*\n\nQuel élément veux-tu placer ici ?`,
            type: 'multiple_choice',
            choices,
            correctResponse: `✨ *Tu poses délicatement l'élément sur le cercle*\n\nChapelier: "Oui ! C'est... c'est exactement ça !"\n\n*ses yeux s'illuminent*\n\n"Continue comme ça..."`,
            incorrectResponse: `❌ *L'élément ne semble pas à sa place...*\n\nChapelier: "Non, non... ça ne va pas là. Réfléchis bien."\n\n*il tremble un peu*\n\n"La couleur du cercle... elle te dit quelque chose ?"`,
            canRetry: false,
            onCorrect: [
              {
                type: 'removeFromInventory',
                params: { itemId: requiredItemId },
              },
              {
                type: 'solveRiddle',
                params: { riddleId: placedRiddleId },
              },
            ],
          });
        }
        // Load room (portal teleportation)
        if (action.type === 'loadRoom') {
          const { roomId, blockedMessage } = action.params as { roomId: string; blockedMessage?: string };
          // Check if portal is active (for conditional portals)
          if (dynamicProperties.activateAfterRiddleSolved && !dynamicProperties.isActive) {
            showMessage(blockedMessage || 'Ce portail n\'est pas encore actif... Parle d\'abord à l\'Oracle.');
            return;
          }
          // Check if portal requires quest completion
          if (dynamicProperties.activateOnQuestComplete && !dynamicProperties.isActive) {
            showMessage(blockedMessage || 'Ce portail s\'ouvrira une fois ta mission accomplie ici...');
            return;
          }
          // Check if portal requires room completion
          if (dynamicProperties.activateAfterRoomCompleted && !dynamicProperties.isActive) {
            showMessage(blockedMessage || 'Ce portail s\'ouvrira quand tu auras terminé le monde précédent...');
            return;
          }
          if (roomId) {
            // Small delay for visual effect
            setTimeout(() => loadRoom(roomId), 300);
          }
        }
      });
    } else {
      showMessage(`Tu examines ${object.name}.`);
    }
  }, [object, interactWithObject, showMessage, openDoor, addToInventory, hideObject, openDoorWithItem, showRiddle, hasInventoryItem, giveItemToNPC, loadRoom, dynamicProperties, completedRooms, solvedRiddles, mountBike, dismountBike]);

  const handleClick = useCallback((e: ThreeEvent<MouseEvent>) => {
    // For non-interactive objects, convert click to floor movement
    if (!isInteractive) {
      // Get the click point on the object and use it as target position
      const point = e.point;
      setTargetPosition([point.x, 0, point.z]);
      setPendingInteraction(null); // Clear any pending interaction
      return; // Don't stopPropagation - let floor also handle if needed
    }

    // For interactive objects, stop propagation and handle interaction
    e.stopPropagation();

    // Calculate distance to object
    const dx = object.position[0] - player.position[0];
    const dz = object.position[2] - player.position[2];
    const distance = Math.sqrt(dx * dx + dz * dz);
    const interactionDist = getInteractionDistance(object);

    // If close enough, interact immediately
    if (distance <= interactionDist + 0.5) {
      setPendingInteraction(null); // Clear any pending interaction
      executeInteraction();
    } else {
      // Too far - walk towards the object and set pending interaction
      const [targetX, targetZ] = getInteractionPosition(
        object,
        player.position[0],
        player.position[2]
      );
      setTargetPosition([targetX, 0, targetZ]);
      setPendingInteraction(object.id); // Store object ID to interact with when arrived
    }
  }, [object, isInteractive, player.position, setTargetPosition, executeInteraction, setPendingInteraction]);

  const handlePointerOver = useCallback(() => {
    if (isInteractive) {
      setHovered(true);
      document.body.style.cursor = 'pointer';
    }
  }, [isInteractive]);

  const handlePointerOut = useCallback(() => {
    setHovered(false);
    document.body.style.cursor = 'default';
  }, []);

  // When occluding, disable raycasting on all child meshes to allow clicks to pass through
  useEffect(() => {
    if (!groupRef.current || isHidden) return;

    // Store original raycast functions to restore later
    const originalRaycasts = new Map<THREE.Object3D, typeof THREE.Object3D.prototype.raycast>();

    groupRef.current.traverse((child) => {
      if ((child as THREE.Mesh).isMesh) {
        if (isOccluding) {
          // Store original and disable
          originalRaycasts.set(child, child.raycast);
          child.raycast = noRaycast as unknown as typeof child.raycast;
        }
      }
    });

    return () => {
      // Restore original raycast functions
      originalRaycasts.forEach((originalRaycast, child) => {
        child.raycast = originalRaycast;
      });
    };
  }, [isOccluding, isHidden]);

  // Don't render if object is hidden (picked up, etc.)
  // This check is after all hooks to comply with React's rules of hooks
  if (isHidden) {
    return null;
  }

  // Render based on modelId
  return (
    <group
      ref={groupRef}
      position={object.position}
      rotation={object.rotation}
      scale={object.scale}
      onClick={isOccluding ? undefined : handleClick}
      onPointerOver={isOccluding ? undefined : handlePointerOver}
      onPointerOut={isOccluding ? undefined : handlePointerOut}
    >
      <ObjectModel
        modelId={object.modelId}
        world={world}
        hovered={hovered}
        interactive={isInteractive}
        properties={dynamicProperties}
        objectId={object.id}
        isOccluding={isOccluding}
      />
    </group>
  );
}

interface ObjectModelProps {
  modelId: string;
  world: WorldType;
  hovered: boolean;
  interactive: boolean;
  properties: Record<string, unknown>;
  objectId?: string;
  isOccluding?: boolean;
}

function ObjectModel({
  modelId,
  world,
  hovered,
  interactive,
  properties,
  objectId,
  isOccluding = false,
}: ObjectModelProps) {
  const colors = world === 'real' ? COLORS.real : COLORS.spectral;
  const { openedDoors } = useGameStore();
  const doorRef = useRef<Group>(null);
  const doorRotation = useRef(0);
  const currentOpacity = useRef(1);

  // Animate door opening
  const isDoorOpen = objectId ? openedDoors.has(objectId) : false;

  // Target opacity when occluding
  const targetOpacity = isOccluding ? 0.3 : 1;

  useFrame((_, delta) => {
    if (modelId === 'animated_door' && doorRef.current) {
      const targetRotation = isDoorOpen ? -Math.PI / 2 : 0;
      doorRotation.current += (targetRotation - doorRotation.current) * delta * 3;
      doorRef.current.rotation.y = doorRotation.current;
    }

    // Smooth opacity transition
    currentOpacity.current += (targetOpacity - currentOpacity.current) * delta * 8;
  });

  // Get current opacity for rendering (used by models that support it)
  const opacity = isOccluding ? 0.3 : 1;

  // Check if model exists in the modular registry first
  const RegistryModel = getModelComponent(modelId);
  if (RegistryModel) {
    return (
      <RegistryModel
        world={world}
        hovered={hovered}
        interactive={interactive}
        properties={properties}
        objectId={objectId}
        isOccluding={isOccluding}
        opacity={opacity}
      />
    );
  }

  // Fallback for unknown models - all models should be in the registry
  return (
    <mesh position={[0, 0.5, 0]} castShadow receiveShadow>
      <boxGeometry args={[1, 1, 1]} />
      <meshStandardMaterial
        color={colors.primary}
        roughness={0.7}
      />
    </mesh>
  );
}
